import type { ConfigItem, DeviceFieldCalibration } from '@/types/devices'
import { getConfigByKey } from './device-config'

export interface UserCalibration {
  name: string
  multiFactor?: number
  offset?: number
  ascCal?: boolean
}

const userCalSortOrder: Record<string, number> = {
  pm25_raw: 0,
  pm10_raw: 1,
  temp: 2,
  rh: 3,
  baro_inhg: 4,
  co2: 5,
  tvoc: 6,
  co: 7,
  ch2o: 8,
  so2: 9,
  o3: 10,
  no2: 11
}

const sortUserCals = (a: UserCalibration, b: UserCalibration): number => {
  return userCalSortOrder[a.name] > userCalSortOrder[b.name] ? 1 : -1
}

export const AUTOCAL_SENSORS: string[] = ['co2']
export const DEFAULT_SLOPE: number = 1
export const DEFAULT_OFFSET: number = 0
export const DEFAULT_AUTOCAL: boolean = true

/**
 * Gets saved user calibrations
 * @param sensorConfigs - Config of device (array of key-value pairs)
 * @param modelCalibrationMeta - Calibration metadata keyed by field name
 * @returns Array of device user calibrations sorted by sensor order
 */
export const getUserCals = (
  sensorConfigs: ConfigItem[],
  modelCalibrationMeta: Record<string, DeviceFieldCalibration>
): UserCalibration[] => {
  return Object.entries(modelCalibrationMeta)
    .map(([fieldName, calMeta]) => {
      switch (fieldName) {
        case 'pm10_raw':
          return getPm10Cal(sensorConfigs, calMeta)
        case 'pm25_raw':
          return getPm25Cal(sensorConfigs, calMeta)
        case 'baro_inhg':
          return getBaroCal(sensorConfigs)
        default:
          return getCal(sensorConfigs, calMeta, fieldName)
      }
    })
    .sort(sortUserCals)
}

const getPm10Cal = (sensorConfigs: ConfigItem[], calMeta: DeviceFieldCalibration): UserCalibration => {
  const cal: UserCalibration = { name: 'pm10_raw' }
  cal.multiFactor = (getConfigByKey(sensorConfigs, 'mcpm10_userslope') as number) ?? DEFAULT_SLOPE
  if (calMeta.offset) {
    cal.offset = (getConfigByKey(sensorConfigs, 'mcpm10_useroffset') as number) ?? DEFAULT_OFFSET
  }
  return cal
}

const getPm25Cal = (sensorConfigs: ConfigItem[], calMeta: DeviceFieldCalibration): UserCalibration => {
  const cal: UserCalibration = { name: 'pm25_raw' }
  cal.multiFactor = (getConfigByKey(sensorConfigs, 'mcpm2x5_userslope') as number) ?? DEFAULT_SLOPE
  if (calMeta.offset) {
    cal.offset = (getConfigByKey(sensorConfigs, 'mcpm2x5_useroffset') as number) ?? DEFAULT_OFFSET
  }
  return cal
}

const getBaroCal = (sensorConfigs: ConfigItem[]): UserCalibration => {
  const cal: UserCalibration = { name: 'baro_inhg' }
  cal.offset = (getConfigByKey(sensorConfigs, 'baro_useroffset') as number) ?? DEFAULT_OFFSET
  return cal
}

const getCal = (
  sensorConfigs: ConfigItem[],
  calMeta: DeviceFieldCalibration,
  fieldName: string
): UserCalibration => {
  const cal: UserCalibration = { name: fieldName }
  if (calMeta.offset) {
    cal.offset =
      (getConfigByKey(sensorConfigs, fieldName.concat('_useroffset')) as number) ?? DEFAULT_OFFSET
  }
  if (calMeta.multiplicationFactor) {
    cal.multiFactor =
      (getConfigByKey(sensorConfigs, fieldName.concat('_userslope')) as number) ?? DEFAULT_SLOPE
  }
  if (AUTOCAL_SENSORS.includes(fieldName)) {
    const autoCal = getConfigByKey(sensorConfigs, fieldName.concat('_asc_cal'))
    cal.ascCal = autoCal !== null ? (autoCal as boolean) : DEFAULT_AUTOCAL
  }
  return cal
}
